import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects"
import { DELETE_ADMIN_TWILIO_NUMBER, EXPORT_ADMIN_TWILIO_NUMBERS, GET_ADMIN_TWILIO_NUMBERS } from "./actionTypes"
import {
  getAdminTwilioNumbersSuccess,
  getAdminTwilioNumbersError,
  deleteAdminTwilioNumberSuccess,
  deleteAdminTwilioNumberError,
  exportAdminTwilioNumbersSuccess,
  exportAdminTwilioNumbersError,
} from "./actions"
import { genericDeleteRequest, genericGetRequest_2, genericPostRequest } from "../../helpers/sf_backend_helper"
import { AppConfiguration } from "config"
import { bsh_getAdminUserDetails } from "helpers/sf_browser_storage_helper"
import { showExportEnqueuedToast } from "helpers/export_toast_helper"
import { buildCampaignAnalysisCacheKey as buildPaginationCacheKey, showErrorToast } from "helpers/sf_utils"

const inFlightTwilioRequests = new Set()

function* onGetAdminTwilioNumbers(action) {
  const useCache = Boolean(action.payload?.useCache)
  const queryParams = action.payload?.queryParams || {}
  const requestedPage = parseInt(queryParams.page_number, 10) || 1
  const cacheKey = useCache ? buildPaginationCacheKey(queryParams) : null
  const requestKey = `${cacheKey}:${requestedPage}`

  if (useCache) {
    const cachedPage = yield select(
      (state) => state.AdminTwilioNumbers.page_cache?.[cacheKey]?.[requestedPage]
    )
    if (cachedPage) return
    if (inFlightTwilioRequests.has(requestKey)) return
    inFlightTwilioRequests.add(requestKey)
  }

  const user = bsh_getAdminUserDetails()
  try {
    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/admin_twilio_numbers`,
      queryParams,
      {
        "Authorization": user.token,
        'Content-Type': 'application/json',
      }
    )
    yield put(getAdminTwilioNumbersSuccess(response, { queryParams, useCache }))
  } catch (error) {
    yield put(getAdminTwilioNumbersError(error))
  } finally {
    if (useCache) {
      inFlightTwilioRequests.delete(requestKey)
    }
  }
}

function* onDeleteAdminTwilioNumber(action) {
  const user = bsh_getAdminUserDetails()
  try {
    const response = yield call(
      genericDeleteRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/admin_twilio_numbers/${action?.payload?.id || null}`,
      {
        body: JSON.stringify({
          ...action.payload
        }),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token,
        }
      }
    )
    yield put(deleteAdminTwilioNumberSuccess(response))
  } catch (error) {
    yield put(deleteAdminTwilioNumberError(error))
  }
}

function* onExportAdminTwilioNumbers(action) {
  const user = bsh_getAdminUserDetails()
  const queryParams = action.payload || {}
  try {
    yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}/api/v1/admin_twilio_numbers/export`,
      {
        body: JSON.stringify({
          filters: queryParams.filters || {},
          sort_column: queryParams.sort_column,
          sort_direction: queryParams.sort_direction,
        }),
        headers: {
          "Content-Type": "application/json",
          "Authorization": user.token,
        },
      }
    )
    yield put(exportAdminTwilioNumbersSuccess())
    showExportEnqueuedToast({ reportExportsAvailable: true })
  } catch (error) {
    yield put(exportAdminTwilioNumbersError(error))
    showErrorToast(error, 'Failed to queue Twilio Numbers export.')
  }
}


function* AdminTwilioNumbersSaga() {
  yield takeLatest(GET_ADMIN_TWILIO_NUMBERS, onGetAdminTwilioNumbers)
  yield takeEvery(DELETE_ADMIN_TWILIO_NUMBER, onDeleteAdminTwilioNumber)
  yield takeEvery(EXPORT_ADMIN_TWILIO_NUMBERS, onExportAdminTwilioNumbers)
}

export default AdminTwilioNumbersSaga
